import React, { useMemo } from "react";
import { ChangeEvent, useReducer, useState, useEffect } from "react";

import axios from "axios";
import { Link, useLoaderData, useNavigate, Form, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Table,
  Input,
  Toggle,
  Checkbox,
  Collapse,
  Select,
  Dropdown,
  Divider,
  Badge,
  Tooltip,
} from "react-daisyui";
import Survey from "../../interfaces/surveys";
import StudyFormData, {defaultStudyFormData} from "../../interfaces/StudyFormData";
import { LoaderData } from "../../types/LoaderData";
import CohortFormData from "./components/create/CohortFormData";
import IndividualFormData from "./components/create/IndividualFormData";
import {
  MeasureGroup,
  MeasureType,
  OptionalMeasure,
} from "../../interfaces/OptionalMeasures";

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import validateStudyForm from "./components/validateStudyForm";
import ErrorMessage from "./components/ErrorMessage";
import ChangeMeasureGroupConfirmModal from "./components/ChangeMeasureGroupConfirmModal";
import FormConfirmModal from "./components/FormConfirmModal";
import CheckToggleOnStudyForm from "./components/CheckToggleOnStudyForm";
import Demographic from "../../interfaces/demographic";
import fetchData from "../../utils/getAllPaginated";
import ChangeDemographicConfirmModal from "./components/ChangeDemographicConfirmModal";

interface ErrorToastProps {
  title: string;
  error: {
    message: string;
    response?: {
      data?: Record<string, string[]>;
    };
  };
}

const ErrorToast: React.FC<ErrorToastProps> = ({ title, error }) => {
  const errTitle = error.message;
  const errBody = error.response?.data || {};

  return (
    <div className=" break-words">
      <h1 className="text-color-primary">{title}</h1>
      <p>Backend error: {errTitle}</p>
      <div>
        <ul className="mt-2">
            {typeof errBody === "string" ? (
          <li className="text-wrap">{errBody}</li> // If the error is a single string
        ) : (
          Object.entries(errBody || {}).map(([key, messages]) => (
            <li key={key}>
              <ul className="text-wrap">
                {Array.isArray(messages) ? (
                  messages.map((message, index) => (
                    <li className="text-wrap" key={index}>{message}</li>
                  ))
                ) : (
                  <li className="text-wrap">{messages}</li> // If it's a string, not an array
                )}
              </ul>
            </li>
          ))
        )}
        </ul>
      </div>
    </div>
  );
};

// Function to trigger the toast
export const showErrorToast = (title: string, error: ErrorToastProps["error"]) => {
  toast.error(<ErrorToast title={title} error={error} />, { autoClose: 30000 });
};

export async function loader({ request, params }: any) {
  const demographics = await fetchData("/api/v1/demographic");
  // Coerce to boolean that studyid exists in url params
  const isEdit = !!params?.studyId;

  // Not editing, only need demographics
  if (!params?.studyId){
    return { demographics, isEdit };
  }

  // Get current study and initialise form data object
  const study = await (await axios.get(`/api/v1/study/${params.studyId}`)).data

  // Preload other attributes for the study such as measure group data and optional measures
  const measureGroup = await (await axios.get(`/api/v1/measure-group/${study.group}`)).data

  const editMeasureGroups = await fetchData('/api/v1/measure-group/', `&target_demo_id=${measureGroup.target_demo_id}`)

  const editOptionalMeasures = await (await axios.get(`/api/v1/measure-group/${study.group}/optional_measures/`)).data

  const editDemographic = await (await axios.get(`/api/v1/demographic/${measureGroup.target_demo_id}`)).data;

  const studyData: StudyFormData = {
    id: study.id,
    title: study.title,
    description: study.description,
    ethic: study.ethic,
    school_study: study.school_study,
    school_name: study.school_name,
    school_postcode: study.school_postcode,
    school_category: study.school_category,
    ethnic_info: study.ethnic_info,
    SES_info: study.SES_info,
    yr_level: study.yr_level,
    other_info: study.other_info,
    created: study.created,
    properties: study.properties,
    demographic: editDemographic,
    group: study.group,
    measures: study.measures,
    cohort_items: study.cohort_items,
    individual_items: study.individual_items,
    active: study.active,
    questions: study.questions,
    demographics: study.demographics,
    intro_consent: study.intro_consent,
    base: study.base,
    constant: study.constant
  };

  return { demographics, editDemographic, editMeasureGroups, isEdit, studyData, editOptionalMeasures }

}

export async function action({ request, params }: any) {
}

interface SchoolCategoryOptionsProps {
  formValue: StudyFormData;
  handleSelectChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  isStudyActive: boolean;
  handleInputChange: any
}


const SchoolCategoryOptions: React.FC<SchoolCategoryOptionsProps> = ({
  formValue,
  handleSelectChange,
  isStudyActive,
  handleInputChange
}) => {
  return (
    <div className="">
      <div className="flex w-full mb-4">
        <label htmlFor="school-gender" className="mr-2">
          Which of the following applies to this school?
        </label>
        <Select
          id="school-gender"
          name="school_category"
          value={formValue.school_category}
          onChange={handleSelectChange}
          className="ml-auto"
          disabled={isStudyActive}
        >
          <option value="co-educational">Co-Educational</option>
          <option value="single-sex-male">
            Single-Sex School: Male Students Only
          </option>
          <option value="single-sex-female">
            Single-Sex School: Female Students Only
          </option>
        </Select>
      </div>
      {formValue.school_category === "co-educational" && (
        <div className="w-full">
          <label className="label">
            <span className="label-text">
              What is the gender information relevant to this school?
            </span>
          </label>
          <Input
            disabled={isStudyActive}
            value={formValue.other_info}
            name="other_info"
            type="text"
            className="mb-2 w-full"
            onChange={(e) => handleInputChange(e)}
          />
        </div>
      )}

      {(formValue.school_category === "co-educational" ||
        formValue.school_category === "single-sex-male" ||
        formValue.school_category === "single-sex-female") && (
        <>
          <div className="w-full">
            <label className="label">
              <span className="label-text">
                What is the ethnic information relevant to this school?
              </span>
            </label>
            <Input
              disabled={isStudyActive}
              value={formValue.ethnic_info}
              name="ethnic_info"
              type="text"
              className="mb-2 w-full"
              onChange={(e) => handleInputChange(e)}
            />
          </div>
          <div className="w-full">
            <label className="label">
              <span className="label-text">
                What is the school's socio-economic (SES) information relevant
                to this school?
              </span>
            </label>
            <Input
              disabled={isStudyActive}
              value={formValue.SES_info}
              name="SES_info"
              type="text"
              className="mb-2 w-full"
              onChange={(e) => handleInputChange(e)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default function Create() {
  const { demographics, editDemographic, editMeasureGroups, isEdit, studyData, editOptionalMeasures } = useLoaderData() as LoaderData<typeof loader>;

  const navigate = useNavigate();
  const { studyId } = useParams();

  const [schoolStudy, setSchoolStudy] = useState(!!studyData?.school_name);

  // Measure Group
  const [measureGroups, setMeasureGroups] = useState<Array<MeasureGroup>>(editMeasureGroups ?? []);
  const [optionalMeasures, setOptionalMeasures] = useState<MeasureGroup | null>(editOptionalMeasures ?? null); //API resposne
  const [selectedOptionalMeasure, setSelectedOptionalMeasure] = useState<number[]>(studyData?.measures ?? []);
  const [isConfirmSwitchingGroup, setIsConfirmSwitchingGroup] = useState(false)
  const [pendingGroupChange, setPendingGroupChange] = useState<{ name: string; value: string; } | null>(null);
  const [showSwitchingMeasureGroupConfirmationModal, setShowSwitchingMeasureGroupConfirmationModal] = useState(false);

  // Demographic
  const [isConfirmSwitchingDemographic, setIsConfirmSwitchingDemographic] = useState(false)
  const [pendingDemographicChange, setPendingDemographicChange] = useState<{ name: string; value: string; } | null>(null);
  const [showSwitchingMeasureDemographicConfirmationModal, setShowSwitchingMeasureDemographicConfirmationModal] = useState(false);

  const [demographic, setDemographic] = useState<Demographic | null | undefined>(editDemographic)
  const memoizedDemographic = useMemo(() => demographic, [demographic]);
  const [cohortReport, setCohortReport] = useState<Set<number>>(studyData?.cohort_items ? new Set(studyData.cohort_items) : new Set());
  const [individualReport, setIndividualReport] = useState<Set<number>>(studyData?.individual_items ? new Set(studyData.individual_items) : new Set());
  const [showCohortReport, setShowCohortReport] = useState<boolean>(!!studyData?.cohort_items.length);
  const [showIndividualReport, setShowIndividualReport] = useState<boolean>(!!studyData?.individual_items.length);

  const [error, setError] = useState<string | null>(null);
  const [FormErrors, setFormErrors] = useState({} as any);
  const [FormData, setFormData] = useState<StudyFormData>(studyData ?? defaultStudyFormData);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);

  const [isStudyActive, setIsStudyActive] = useState(studyData?.active ?? false)
  
  //Check Edit mode and assign default data (handles switch from edit to create view)
  useEffect(() => {
    if(!isEdit){
      setFormData(defaultStudyFormData)
      setIsStudyActive(false);
      setMeasureGroups([]);
      setOptionalMeasures(null);
      setSelectedOptionalMeasure([]);
      setIsConfirmSwitchingGroup(false);
      setSchoolStudy(false);
      setShowCohortReport(false);
      setShowIndividualReport(false);
      setDemographic(null);
    }
  }, [isEdit])
  
  const toggleOptionalMeasure = (id: number) => {
    if (selectedOptionalMeasure.includes(id)) {
      setSelectedOptionalMeasure(
        selectedOptionalMeasure.filter((measureId) => measureId !== id)
      );
    } else {
      setSelectedOptionalMeasure([...selectedOptionalMeasure, id]);
    }
  };

  const toggleIndividualReport = () => { setShowIndividualReport(!showIndividualReport); };

  const toggleCohortReport = () => { setShowCohortReport(!showCohortReport); };

  const handleCloseModal = () => { setShowSwitchingMeasureGroupConfirmationModal(false); };

  const handleCloseDemographicModal = () => { setShowSwitchingMeasureDemographicConfirmationModal(false); };

  const FormAConfirmModal = () => { setConfirmModalOpen(true); }

  const handleFormSubmit = async () => {
    console.log(`FormData: ${FormData}`)
    const updatedFormData = {
      ...FormData,
      cohort_items: Array.from(cohortReport),
      individual_items: Array.from(individualReport),
      measures: selectedOptionalMeasure,
    };
    console.log(updatedFormData)
    // Update state with the updated data
    setFormData(updatedFormData);
    
    const validateFormData= {
      ...updatedFormData,
      school_study: schoolStudy,
      cohort_report: showCohortReport,
      individual_report: showIndividualReport,
    }
    console.log("validateFormData", validateFormData)
    const validationErrors = validateStudyForm(validateFormData);
    setFormErrors(validationErrors);

    const errorElements = [];

    for (const key in validationErrors) {
      if (validationErrors.hasOwnProperty(key)) {
        errorElements.push(<li key={key}>{validationErrors[key]}</li>);
      }
    }
    
    const hasError = errorElements.length > 0;

    //Frontend validation and error message
    hasError && toast.error(
      <div>
        <p>Validation errors </p>
        <ul  className="list-disc pl-4 min-w-[350px] max-w-md">
          {errorElements}
        </ul>
      </div>,
      {
        autoClose: 30000,
      }
    );
    hasError && setConfirmModalOpen(false);
    
    //Check all toggle button and empty the fields if button is switched to false before saving to database.
    const finalFormData = CheckToggleOnStudyForm(validateFormData)
    console.log("finalFormData", finalFormData)

    let hasFormActionError = false;

    if (!hasError && isEdit && !isStudyActive) {
      //Update new form 
      hasFormActionError = await updateFormAction(studyId, finalFormData)
    }

    if (!hasError && !isEdit && !isStudyActive) {
      //Create a new form 
      hasFormActionError = await submitFormAction(finalFormData)
      console.log(`Create new study form: ${finalFormData}`)
    }
    
    if (!hasError && isEdit && isStudyActive) {
       //Patch the activated form
      const patchData = {
        cohort_items: finalFormData.cohort_items, 
        individual_items: finalFormData.individual_items,
      }
      hasFormActionError = await patchFormAction(studyId, patchData)
      console.log(`Update study form: ${finalFormData}`)
    }

    if(!hasError && !hasFormActionError){
      navigate('/studies');
    }
  };

  const submitFormAction = (data: any ) => {
    return axios.post('/api/v1/study/', data )
        .then((response) => {
          // Assuming your response contains a success status
          if (response.status === 201) {
            
            toast.success('Form submitted successfully!', {
              autoClose: 30000,
              // onClose: () => this.props.history.push('/studies'),
            });

            setConfirmModalOpen(false)
          }
          return false
        })
        .catch((error) => {
          showErrorToast("Failed to Update Form", error);
          return true;
        }).catch((error) => {
          toast.error(`Unexpected error encountered - ${error}`)
          return true;
        }).finally(() => {
          setConfirmModalOpen(false)
        });
  } 

const updateFormAction = (id: any, data: any) => {
  console.log('updateForm', FormData)
    return axios.put(`/api/v1/study/${id}/`, data)
        .then((response) => {
          // Assuming your response contains a success status
          if (response.status === 200) {
            toast.success('Form updated successfully!', {
              autoClose: 30000,
            });
            setConfirmModalOpen(false)
          } 
          return false;
        })
        .catch((error) => {
          showErrorToast("Failed to Update Form", error);
          return true;
        }).catch((error) => {
          toast.error(`Unexpected error encountered - ${error}`)
          return true;
        }).finally(() => {
          setConfirmModalOpen(false)
        });
  }

const patchFormAction =  (id: any, data: any) => {
  console.log("patchFormAction", data)
    return axios.patch(`/api/v1/study/${id}/`, data)
        .then((response) => {
          // Assuming your response contains a success status
          if (response.status === 200) {
            toast.success('Form patched successfully!', {
              autoClose: 30000,
            });
            setConfirmModalOpen(false)
          }
          return false;
        })
        .catch((error) => {
          showErrorToast("Failed to Patch Form", error);
          return true;
        }).catch((error) => {
          toast.error(`Unexpected error encountered - ${error}`)
          return true;
        }).finally(() => {
          setConfirmModalOpen(false)
        });
  }

  const fetchMeasureGroups = (demo_id: number) => {
    //reset all the optionsalmeasures because it is switching to another group
    setOptionalMeasures(null);
    setSelectedOptionalMeasure([]);

    return fetchData('/api/v1/measure-group/', `&target_demo_id=${demo_id}`).then((measuresGroups)=>{
      setMeasureGroups(measuresGroups);
    })
  };


  const fetchOptionalMeasures = (group: number) => {
    //reset all the optionsalmeasures becaue it switching to another group
    setOptionalMeasures(null);
    setSelectedOptionalMeasure([]);
    return axios
      .get(`/api/v1/measure-group/${group}/optional_measures/`)
      .then((res) => setOptionalMeasures(res.data))
      .catch((err) => {
        console.error(err);
        setError("Failed to fetch data. Please try again.");
      });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const updatedFormData = { ...FormData, [name]: value };
    setFormData(updatedFormData);
    
    const newErrors = validateStudyForm(updatedFormData);
    setFormErrors(newErrors);
  };
  
  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    const updatedFormData = { ...FormData, [name]: value };
    setFormData(updatedFormData);
    
    const newErrors = validateStudyForm(updatedFormData);
    setFormErrors(newErrors);
  }

  const confirmGroupChange = () => {
    handleCloseModal();
    setIsConfirmSwitchingGroup(true);
  };

  useEffect(() => {
  if (isConfirmSwitchingGroup && pendingGroupChange) {
    const { name, value } = pendingGroupChange;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
    fetchOptionalMeasures(parseInt(value));
    // Reset the pending group change.
    setPendingGroupChange(null);
  }
}, [isConfirmSwitchingGroup, pendingGroupChange]);

  const handleSelectGroupChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    if (isEdit && !isConfirmSwitchingGroup) { 
      setPendingGroupChange({ name, value });
      setShowSwitchingMeasureGroupConfirmationModal(true);
    } else {
      // Set demographic and remove group selection
      setFormData((prevFormData) => ({ ...prevFormData, [name]: value}));
      fetchOptionalMeasures(parseInt(value));
    }
  }

  const confirmDemographicChange = () => {
    handleCloseDemographicModal();
    setIsConfirmSwitchingDemographic(true);
    setCohortReport(new Set());
    setIndividualReport(new Set());
  };

  useEffect(() => {
    if (isConfirmSwitchingDemographic && pendingDemographicChange) {
      const { name, value } = pendingDemographicChange;

      // Check if value is truthy, if not fail out and clear the form
      if(!value){
        setDemographic(null);
        setFormData((prevFormData) => ({ ...prevFormData, [name]: value, ['group']: "" }));
        setOptionalMeasures(null);
        setSelectedOptionalMeasure([]);
        setPendingDemographicChange(null);
        return
      }

      // Try to get measure groups first as a failure will prevent login afterwards
      fetchMeasureGroups(parseInt(value))
      .catch((err) => {
        toast.error(`Could not load measure groups - ${err?.message}`)
        setError("Failed to fetch data. Please try again.");
      })
      .then(()=>{
        const target_demo = demographics.find((demo: Demographic)=>(demo.id == parseInt(value)))
        if(!target_demo){
          throw new Error('Could not find matching demographic in demographics list')
        }
        setDemographic(target_demo);
        setFormData((prevFormData) => ({ ...prevFormData, [name]: value, ['group']: "" }));
        // Reset the pending group change.
        setPendingDemographicChange(null);
      })
      .catch((err)=>{
        toast.error(`Failed to set demographic - ${err}`)
      })
    }
  }, [isConfirmSwitchingDemographic, pendingDemographicChange]);

  const handleSelectDemographicChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;

    // If editing, prep for modal switch confirmation and swap
    if (isEdit && !isConfirmSwitchingDemographic) {
      setPendingDemographicChange({ name, value });
      setShowSwitchingMeasureDemographicConfirmationModal(true);
    // Otherwise change values directly
    } else {
      // Is value truthy, i.e. does it have an id
      if(value){
        // Find the matching demographic
        const target_demo = demographics.find((demo: Demographic)=>(demo.id == parseInt(value)))
        // Fail out if no match is found
        if(!target_demo){
          toast.error(`Failed to set demographic - Could not find matching demographic`)
          setDemographic(null);
          setFormData((prevFormData) => ({ ...prevFormData, [name]: value, ['group']: "" }));
          setOptionalMeasures(null);
          setSelectedOptionalMeasure([]);
          return
        }
        setDemographic(target_demo);
        setFormData((prevFormData) => ({ ...prevFormData, [name]: value, ['group']: "" }));
        fetchMeasureGroups(parseInt(value));
      // Nope, then clear the depedent values for groups and optional measures
      }else{
        setDemographic(null);
        setFormData((prevFormData) => ({ ...prevFormData, [name]: value, ['group']: "" }));
        setOptionalMeasures(null);
        setSelectedOptionalMeasure([]);
      }

    }
  }

  return (
    <>
      <div className="card w-[80%] ml-auto mr-auto min-w-min bg-base-100 shadow-xl">
        <Form
          className="flex flex-col gap-4 pl-4 pr-4"
          method="post"
          onSubmit={handleFormSubmit}
          id="study-form"
        >
          <h1 className="font-bold font-sans break-normal pb-2 text-3xl md:text-4xl">
            {!isEdit ? `Create Study` : ` Edit Study` }
            {isEdit && isStudyActive && <Tooltip className="cursor-pointer text-left" position="right" message="The study has already been activated and only allows updates in the Reports section."><Badge className="ml-2" size="md" color="success">Active</Badge></Tooltip>}
          </h1> 

          <h5 className="mt-0 mb-0 text-lg font-bold text-accent">
            Study Details
          </h5>
          <div className="flex flex-col gap-4">
            <label className="label">
              <h6 className="mt-0 mb-0 text-base font-medium">Title</h6>
              <span className="label-text-alt text-error">required</span>
            </label>
            <Input 
              name="title" 
              value={FormData.title}
              onChange={(e) => handleInputChange(e)}
              disabled={isStudyActive}
            />
            {FormErrors?.title && (
              <span className="text-red-500 text-sm">{FormErrors.title}</span>
            )}

            <label className="label">
              <h6 className="mt-0 mb-0 text-base font-medium">Description</h6>
              <span className="label-text-alt text-error">required</span>
            </label>
            <Input name="description" 
              value={FormData.description}
              onChange={(e) => handleInputChange(e)}
              disabled={isStudyActive}
            />
            {FormErrors?.description && (
              <span className="text-red-500 text-sm">{FormErrors.description}</span>
            )}

            <label className="label">
              <h6 className="mt-0 mb-0 text-base font-medium">Ethics ID</h6>
              <span className="label-text-alt text-error">required</span>
            </label>
            <Input name="ethic" 
               value={FormData.ethic}
               onChange={(e) => handleInputChange(e)}
               disabled={isStudyActive}
            />
            {FormErrors?.ethic && (
              <span className="text-red-500 text-sm">{FormErrors.ethic}</span>
            )}
          </div>

          <h5 className="mt-0 mb-0 text-lg font-bold text-accent">
            School Options
          </h5>
          <div className="flex flex-col gap-4 m-4">
            <div className="flex flex-row gap-4">
              <span className="mt-0 mb-0 text-base font-medium">
                School based study
              </span>

              <div className="flex flex-row space-x-1 items-center">
                <span className="text-xs">No</span>
                <Toggle
                  id="schoolStudyToggle"
                  color="success"
                  checked={schoolStudy}
                  value={schoolStudy.toString()}
                  onChange={() => setSchoolStudy((oldState) => !oldState)}
                  disabled={isStudyActive}
                />
                <span className="text-xs">Yes</span>
              </div>
            </div>

            {schoolStudy && (
              <>
                <div className="flex flex-col gap-4">
                  <h6 className="mt-0 mb-0 text-base font-medium">
                    School Name
                  </h6>
                  <Input name="school_name" 
                    value={FormData.school_name}
                    onChange={(e) => handleInputChange(e)}
                    disabled={isStudyActive}
                  />
                  {FormErrors.school_name && (
                    <span className="text-red-500 text-xs">
                      {FormErrors.school_name}
                    </span>
                  )}
                  <h6 className="mt-0 mb-0 text-base font-medium">
                    School Postcode
                  </h6>
                  <Input name="school_postcode" 
                    id="school_postcode"
                    value={FormData.school_postcode}
                    onChange={(e) => handleInputChange(e)}
                    disabled={isStudyActive}
                  />
                  {FormErrors.school_postcode && (
                    <span className="text-red-500 text-xs">
                      {FormErrors.school_postcode}
                    </span>
                  )}
                  <label htmlFor="yr_level" className="label">
                  <span className="label-text">
                    Which of the following year levels will be involved in this
                    study?
                  </span>
                </label>
                <Select
                  value={FormData.yr_level}
                  onChange={handleSelectChange}
                  id="yr_level"
                  name="yr_level"
                  className="select select-bordered w-full max-w-xs"
                  disabled={isStudyActive}
                >
                  <Select.Option value="">-- Please choose an option --</Select.Option>
                  <Select.Option value="Prep">Prep</Select.Option>
                  <Select.Option value="1">Year 1</Select.Option>
                  <Select.Option value="2">Year 2</Select.Option>
                  <Select.Option value="3">Year 3</Select.Option>
                  <Select.Option value="4">Year 4</Select.Option>
                  <Select.Option value="5">Year 5</Select.Option>
                  <Select.Option value="6">Year 6</Select.Option>
                  <Select.Option value="7">Year 7</Select.Option>
                  <Select.Option value="8">Year 8</Select.Option>
                  <Select.Option value="9">Year 9</Select.Option>
                  <Select.Option value="10">Year 10</Select.Option>
                  <Select.Option value="11">Year 11</Select.Option>
                  <Select.Option value="12">Year 12</Select.Option>
                </Select>
                {FormErrors.yr_level && (
                  <span className="text-red-500 text-sm">
                    {FormErrors.yr_level}
                  </span>
                )}
                </div>
                <Divider />
                  <SchoolCategoryOptions
                    formValue={FormData}
                    handleSelectChange={handleSelectChange}
                    handleInputChange={handleInputChange}
                    isStudyActive={isStudyActive}
                  />
                <Divider />
              </>
            )}
          </div>

          <h5 className="mt-0 mb-0 text-lg font-bold text-accent">
            Study Measures
          </h5>
          <div className="flex flex-col gap-4 m-4">
            <label className="label">
              <h6 className="mt-0 mb-0 text-base font-medium">Study Demographic</h6>
              <span className="label-text-alt text-error">required</span>
            </label>
            <Select
              name="demographic"
              value={demographic ? demographic?.id : ''}
              onChange={handleSelectDemographicChange}
              className="form-select block w-full mt-1"
              disabled={isStudyActive}
            >
              <Select.Option value="">Select a demographic</Select.Option>
              <>
                {
                  demographics.map((demographic: Demographic) => (
                    <Select.Option key={demographic.id} value={demographic.id}>
                        {demographic.title}
                    </Select.Option>
                  ))
                }
              </>
            </Select>
            {FormErrors?.demographic && (
              <span className="text-red-500 text-sm">{FormErrors.demographic}</span>
            )}
            <label className="label">
              <h6 className="mt-0 mb-0 text-base font-medium">Study Group</h6>
              <span className="label-text-alt text-error">required</span>
            </label>
            <Select
              name="group"
              value={FormData.group}
              onChange={handleSelectGroupChange}
              className="form-select block w-full mt-1"
              disabled={isStudyActive || !demographic}
            >
              <Select.Option value="">Select a group</Select.Option>
              <>
              {
                measureGroups.map((mg: MeasureGroup) => (
                  <Select.Option key={mg.id} value={mg.id}>
                      {mg.title}
                  </Select.Option>
                ))
              }
              </>
              </Select>
              {FormErrors?.group && (
                <span className="text-red-500 text-sm">{FormErrors.group}</span>
              )}
              <>
                <h4 className="mt-0 mb-0 text-base font-semibold">
                  Optional Measures
                </h4>
                {optionalMeasures?.optional_measures.map((measureType: MeasureType) => (
                  <div key={measureType.id}>
                    <h6>{measureType.title}</h6>
                    <div className="ml-2" key={measureType.id}>
                      <label className="text-sm" htmlFor={`${measureType.id}`}>
                        {measureType.measures.length ? "": "None"}
                      </label>
                    </div>
                    {measureType.measures.map((measure: OptionalMeasure) => (
                      <div className="ml-2" key={measure.id}>
                        <input
                          type="checkbox"
                          checked={selectedOptionalMeasure?.includes(measure.id)}
                          id={`OptionalMeasure${measure.id}`}
                          onChange={() => toggleOptionalMeasure(measure.id)}
                          className="mr-2"
                          disabled={isStudyActive || !demographic}
                        />
                        <label className="text-sm" htmlFor={`${measure.id}`} title={measure.title}>
                          {measure.display_title ? measure.display_title : measure.title}
                        </label>
                      </div>
                    ))}
                  </div>
                ))}
                {!optionalMeasures?.optional_measures?.length && (<div>No optional measures</div>) }
              </>
          </div>
            <>
            <h5 className="mt-0 mb-0 text-lg font-bold text-accent">Reports</h5>
            <div className="flex flex-col gap-4 m-4">
              <h6 className="mt-0 mb-0 text-xl text-info">Cohort Report</h6>
              <div className="flex flex-row ">
                <span className=" mr-4">
                  Do you want year level cohort Reflect Report to be generated for
                  this study?
                </span>
                <div className="flex flex-row space-x-1 items-center">
                  <span className="text-xs">No</span>
                  <Toggle id="showCohortReportToggle" checked={showCohortReport && !!demographic} color="success" onClick={toggleCohortReport} disabled={!demographic}/>
                  <span className="text-xs">Yes</span>
                </div>
              </div>
              {FormErrors.cohort_items && (
                    <span className="text-red-500 text-sm">
                      {FormErrors.cohort_items}
                    </span>
                  )}
              {showCohortReport && !!demographic && (
                <CohortFormData
                  selectedRow={cohortReport}
                  setSelectedRow={setCohortReport}
                  showCohortReport={showCohortReport && !!demographic}
                  demographic={memoizedDemographic}
                />
              )}
            </div>
            <Divider />
            <div className="flex flex-col gap-4 m-4">
            <h6 className="mt-0 mb-0 text-xl text-info">
                Individual Reflect Report
              </h6>
              <div className="flex flex-row ">
                <span className="mr-4">
                  Do you want individual Reflect Report to be generated for this
                  study?
                </span>
                <div className="flex flex-row space-x-1 items-center">
                  <span className="text-xs">No</span>
                  <Toggle id="showIndividualReportToggle" checked={showIndividualReport && !!demographic} color="success" onClick={toggleIndividualReport} disabled={!demographic}/>
                  <span className="text-xs">Yes</span>
                </div>
              </div>
              {FormErrors.individual_items && (
                    <span className="text-red-500 text-sm">
                      {FormErrors.individual_items}
                    </span>
                  )}
              {showIndividualReport && !!demographic && (
                <IndividualFormData
                  selectedRow={individualReport}
                  setSelectedRow={setIndividualReport}
                  showIndividualReport={showIndividualReport && !!demographic}
                  demographic={memoizedDemographic}
                />
              )}
            </div>
            </>
          <ToastContainer />
          <FormConfirmModal 
            isOpen={isConfirmModalOpen}
            closeModal={() => setConfirmModalOpen(false)}
            handleConfirm={() => handleFormSubmit()}
            actionDescription={ !isEdit ? `create study` : `update study`}
          />
          <ChangeMeasureGroupConfirmModal
            isOpen={showSwitchingMeasureGroupConfirmationModal}
            closeModal={handleCloseModal}
            handleConfirm={confirmGroupChange}
          />
          <ChangeDemographicConfirmModal
            isOpen={showSwitchingMeasureDemographicConfirmationModal}
            closeModal={handleCloseDemographicModal}
            handleConfirm={confirmDemographicChange}
          />
          <Button id="CreateStudySaveStudyButton" type="button" color="primary" onClick={FormAConfirmModal}>
            Save Study
          </Button>
        </Form>
      </div>
    </>
  );
}
